import React from "react";
import { recordUiEvent } from "../lib/logRingBuffer";
import { renderErrorEvent } from "../lib/telemetry/renderError.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { C } from "../lib/colors.js";
import GlobalStyles from "./GlobalStyles.jsx";
import { Button } from "./ui/Primitives.jsx";
import { IconAlert } from "./ui/Icons.jsx";

/**
 * Catches a render crash and shows a calm full-screen recovery state. The error and its stack
 * go to the console (and the telemetry ring buffer), never to the screen; a local dev build
 * can still open them behind "Show details". Pass `onSendFeedback` to offer a feedback button.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, showDetails: false };
  }
  static getDerivedStateFromError(e) {
    return { error: e };
  }
  componentDidCatch(error, info) {
    console.error("[Sayer] render error:", error, info?.componentStack);
    // Always on (a domain event, not a sub-flag group): the crash itself.
    try {
      recordUiEvent(renderErrorEvent(error, info));
    } catch {
      /* never let telemetry fail the error screen */
    }
  }
  render() {
    if (!this.state.error) return this.props.children;
    const { onSendFeedback } = this.props;
    const canShowDetails = Boolean(import.meta.env?.DEV);
    return (
      <div
        role="alert"
        {...surface("error-screen")}
        style={{
          position: "fixed",
          inset: 0,
          overflowY: "auto",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          boxSizing: "border-box",
          background: C.bg,
          color: C.white,
        }}
      >
        {/* The app's own GlobalStyles unmounted with the crashed tree; mount it here so the
            theme tokens and the button styles are still published. */}
        <GlobalStyles />
        <div
          style={{
            width: 440,
            maxWidth: "100%",
            background: C.panel,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: C.shadowBox,
            padding: 32,
            boxSizing: "border-box",
          }}
        >
          <div style={{ color: C.orange, marginBottom: 16, lineHeight: 0 }}>
            <IconAlert width={24} height={24} />
          </div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C.white }}>
            Something went wrong
          </h1>
          <p style={{ margin: "8px 0 24px", fontSize: 13, lineHeight: 1.5, color: C.grayL }}>
            Sayer hit an unexpected error. Reloading usually fixes it.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              variant="primary"
              {...ui("error-screen.reload")}
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
            {onSendFeedback ? (
              <Button
                {...ui("error-screen.send-feedback")}
                onClick={() => onSendFeedback(this.state.error)}
              >
                Send feedback
              </Button>
            ) : null}
            {canShowDetails ? (
              <Button
                variant="ghost"
                size="sm"
                {...ui("error-screen.details-toggle")}
                onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
                style={{ marginLeft: "auto" }}
              >
                {this.state.showDetails ? "Hide details" : "Show details"}
              </Button>
            ) : null}
          </div>
          {canShowDetails && this.state.showDetails ? (
            <pre
              className="sy-mono"
              style={{
                marginTop: 16,
                maxHeight: 240,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                fontSize: 12,
                color: C.gray,
              }}
            >
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
